<script>
/**
 * Badge "quem respondeu este turno" — spec-010-supervisor-e-agentes-de-dominio
 * [T026][US-7]. Apresentacional puro (sem eventos): a resolução de hub/agente
 * é toda derivada de `constants/Hubs.js` (espelho do backend, `getHub` e
 * `rotuloDoAgente` NUNCA lançam). Contrato: `contracts/ui-components.md` §1.1.
 *
 * Os 4 estados vêm do PROP `estado`, não são inferidos aqui — quem decide se
 * um turno é "vazio" (sem `agent_slug`, turno antigo) ou "erro" é quem monta
 * a mensagem (T027), porque só ali se sabe se o SSE terminou sem
 * `agent-selected`.
 */
import { getHub, rotuloDoAgente } from '@modules/aawz-intelligence-v2/constants/Hubs'
import { ROTULO_AGENTE_GENERICO } from '@modules/aawz-intelligence-v2/constants/TextChat'

export const ESTADO_CARREGANDO = 'carregando'
export const ESTADO_SUCESSO = 'sucesso'
export const ESTADO_VAZIO = 'vazio'
export const ESTADO_ERRO = 'erro'

const ESTADOS_VALIDOS = [
  ESTADO_CARREGANDO,
  ESTADO_SUCESSO,
  ESTADO_VAZIO,
  ESTADO_ERRO,
]

export default {
  name: 'IntelligenceV2AgentBadge',
  props: {
    // `profissional` · `gestao` · `servicos` — valor vindo do SSE/persistido.
    // Nunca validado contra HUBS aqui: `getHub` já cai no default (Hub do
    // Profissional) para qualquer id desconhecido, sem lançar.
    hub: {
      type: String,
      required: true,
    },
    // slug do agente do turno (`_agentes.slug`). Vazio ⇒ badge só com o hub
    // (mesmo comportamento do estado `vazio`).
    agentSlug: {
      type: String,
      default: '',
    },
    // Rótulo humano explícito. Ausente ⇒ derivado do slug por `Hubs.js`
    // (`rotuloDoAgente`) — nunca os dois lidos direto no template, senão a
    // regra de precedência fica implícita em dois lugares.
    agentLabel: {
      type: String,
      default: '',
    },
    // Frase curta do roteamento (tooltip). Só aparece no estado `sucesso`
    // com agente resolvido — nos outros três não haveria o que explicar.
    motivo: {
      type: String,
      default: '',
    },
    estado: {
      type: String,
      default: ESTADO_SUCESSO,
      validator: (valor) => ESTADOS_VALIDOS.includes(valor),
    },
  },
  computed: {
    hubInfo() {
      return getHub(this.hub)
    },
    hubLabel() {
      return this.hubInfo.label
    },
    // `agentLabel` explícito vence; ausente, deriva do slug — mesma
    // precedência declarada no contrato ("`agentLabel` ausente ⇒ derivado do
    // slug por `Hubs.js`"). `rotuloDoAgente` devolve '' para slug
    // desconhecido/ausente, nunca lança.
    rotuloAgente() {
      return this.agentLabel || rotuloDoAgente(this.agentSlug)
    },
    ehCarregando() {
      return this.estado === ESTADO_CARREGANDO
    },
    ehErro() {
      return this.estado === ESTADO_ERRO
    },
    // O estado `vazio` (turno antigo sem `agent_slug`) é o que o CHAMADOR
    // declarou via prop `estado` — não um cálculo derivado de `agentSlug`
    // estar preenchido ou não. Assim um `estado="vazio"` que por engano
    // carregue `agentSlug`/`agentLabel` ainda cai no comportamento do
    // contrato: só o rótulo do hub, sem tooltip.
    mostraAgente() {
      return this.estado === ESTADO_SUCESSO && Boolean(this.rotuloAgente)
    },
    mostraMotivo() {
      return this.mostraAgente && Boolean(this.motivo)
    },
    // No `erro` o turno morreu antes do `agent-selected`, então não há slug a
    // derivar: o rótulo é o `agentLabel` que o CHAMADOR resolveu em cascata
    // (agente ativo da conversa → principal da persona — `chat-message.vue::
    // rotuloDoAgenteSemResolucao`), com o genérico como piso. Era um traço
    // literal ("—"), que ao lado da marca lia como campo quebrado. Continua
    // sem `motivo` e sem repetir o hub: não há confirmação de rota aqui.
    rotuloDoErro() {
      return this.agentLabel || ROTULO_AGENTE_GENERICO
    },
    // Rótulo visível por estado.
    rotuloVisivel() {
      if (this.ehCarregando) return 'Roteando…'
      if (this.ehErro) return this.rotuloDoErro
      if (this.mostraAgente) return this.rotuloAgente
      return this.hubLabel
    },
    // aria-label [ui-components.md §1.1]: "respondido por {agentLabel}, hub
    // {hubLabel}" é o contrato LITERAL do sucesso com agente resolvido. Os
    // outros três estados usam uma frase que não promete um agente que a
    // tela não tem — no erro, o rótulo nomeia quem DEVERIA ter respondido.
    ariaLabel() {
      if (this.ehCarregando) return `roteando resposta, hub ${this.hubLabel}`
      if (this.ehErro) return `${this.rotuloDoErro} não conseguiu responder`
      if (this.mostraAgente) {
        return `respondido por ${this.rotuloAgente}, hub ${this.hubLabel}`
      }
      return `respondido pelo hub ${this.hubLabel}`
    },
    // Cor neutra só no `erro` — "badge neutro, sem promessa de agente"
    // [ui-components.md §1.1]. Sempre var(--…): `icon-svg` não aceita
    // `rgba()` (some silenciosamente — A-57).
    corIcone() {
      return this.ehErro ? 'var(--neutral-700)' : 'var(--primary)'
    },
    // id único no DOCUMENTO (não na instância) — mesmo cuidado de
    // `_base-split-button.vue`: duas badges na mesma tela com o mesmo `_uid`
    // relativo colidiriam no `aria-describedby`.
    tooltipId() {
      return `agent-badge-motivo-${this._uid}`
    },
  },
}
</script>

<template>
  <div
    class="agent-badge d-inline-flex align-center"
    :aria-label="ariaLabel"
    :title="mostraMotivo ? motivo : null"
    :aria-describedby="mostraMotivo ? tooltipId : null"
  >
    <icon-svg
      :name="hubInfo.iconName"
      :size="14"
      :color="corIcone"
      class="mr-1 flex-shrink-0"
    />
    <span
      class="az-text-secondary az-size-12"
      :class="{ 'ia-shimmer-text': ehCarregando }"
    >
      {{ rotuloVisivel }}
    </span>
    <!-- Texto só para leitor de tela: o `title` nativo não é confiável em
         todo leitor de tela / dispositivo touch (não depende de hover,
         conforme o contrato). Mesmo padrão de `_base-split-button.vue`. -->
    <span v-if="mostraMotivo" :id="tooltipId" class="sr-only">{{ motivo }}</span>
  </div>
</template>

<style lang="sass" scoped>
// ⚠️ `user-select: none` pelo mesmo motivo de `raciocinio-linha__anuncio`:
// `sr-only` esconde do olho, não da SELEÇÃO — sem isto o motivo do badge
// viaja junto quando alguém copia a resposta.
.sr-only
  position: absolute
  width: 1px
  height: 1px
  padding: 0
  margin: -1px
  overflow: hidden
  clip: rect(0, 0, 0, 0)
  white-space: nowrap
  border: 0
  user-select: none
</style>
